import { useState } from "react";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface CurrencyToggleProps {
  balance?: number;
  baseCurrency: string;
  availableCurrencies?: string[];
  selectedCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
  disabled?: boolean;
  showBalance?: boolean;
}

export function CurrencyToggle({
  balance,
  baseCurrency,
  selectedCurrency: controlledCurrency,
  onCurrencyChange,
  disabled: externalDisabled = false,
  showBalance = true,
}: CurrencyToggleProps) {
  const [internalCurrency, setInternalCurrency] = useState(baseCurrency);
  const { convert, isLoading, error } = useExchangeRates(baseCurrency);
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  // Determine if component is controlled
  const isControlled = controlledCurrency !== undefined;
  const selectedCurrency = isControlled ? controlledCurrency : internalCurrency;

  // Determine disabled state
  const isDisabled = externalDisabled || isLoading || !!error;

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleCurrencyChange = (currency: string) => {
    if (!isControlled) {
      setInternalCurrency(currency);
    }
    onCurrencyChange?.(currency);
  };

  const getConvertedBalance = (): number => {
    if (!balance) return 0;
    if (selectedCurrency === baseCurrency) {
      return balance;
    }
    try {
      return convert(balance, baseCurrency, selectedCurrency);
    } catch (err) {
      console.error("Currency conversion error:", err);
      return balance;
    }
  };

  const convertedBalance = getConvertedBalance();

  return (
    <div className="space-y-2">
      {/* Live region for currency change announcements */}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true">
        {isLoading && "Loading exchange rates"}
        {!isLoading &&
          !error &&
          selectedCurrency !== baseCurrency &&
          `Currency changed to ${selectedCurrency}`}
        {error && "Error loading exchange rates"}
      </div>

      <div className="flex items-center gap-4">
        <Select
          value={selectedCurrency}
          onValueChange={handleCurrencyChange}
          disabled={isDisabled}>
          <SelectTrigger
            className="w-[120px]"
            aria-label="Select currency for display"
            aria-busy={isLoading}>
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          {isLoadingCurrencies && "Loading Currencies"}
          {!isLoadingCurrencies && !errorLoadingCurrencies ? (
            <SelectContent>
              {availableCurrencies.map((currency) => (
                <SelectItem key={currency} value={currency}>
                  {currency}
                </SelectItem>
              ))}
            </SelectContent>
          ) : (
            "Failed to Load Currencies"
          )}
        </Select>

        {showBalance && balance !== undefined && (
          <div className="text-2xl font-semibold">
            {isLoading ? (
              <span className="text-muted-foreground">Loading...</span>
            ) : (
              <span>{formatCurrency(convertedBalance, selectedCurrency)}</span>
            )}
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Unable to load exchange rates. Please try again later.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
